import * as PIXI from "pixi.js";
import TickerTimeout from "../../utils/TickerTimeout";
import FXManager from "../../Engine/Effects/FXManager";
import { fireLightningBolt } from "../../Engine/Effects/fireLightningBolt";
import { Settings } from "../../settings";
import { isCoinSymbol, isJackpotSymbol, getSymbolZIndex } from "../../SymbolData";


// Reusable point — avoids heap allocation in hot path
const _tempPoint = new PIXI.Point();


// ─── POT Type → Game View Bone Mapping ──────────────────────────────────────

/**
 * Maps feature type letter to the game_view spine attachment bone.
 * Used as trail SOURCE for ejection (POT → grid) and activation (POT → Zeus).
 */
const FEATURE_TO_POT_BONE = {
  M: "attachment_mystery_pot",
  D: "attachment_double_pot",
  C: "attachment_collect_pot",
};

/**
 * Maps feature type letter to ALL Zeus symbolIds on the grid (including variants).
 * M → 13 (cm), 21/22/23 (variants)
 * D → 12 (cd), 20 (cd_mult)
 * C → 14 (cc), 25 (cc_empty)
 */
const FEATURE_TO_ZEUS_IDS = {
  M: [13, 21, 22, 23],
  D: [12, 20],
  C: [14, 25],
};

/**
 * Maps feature type letter to activation animation name on cs spine.
 */
const FEATURE_TO_ACTIVATE_ANIM = {
  M: "CM_Red/activate_red_cm",
  D: "CD_Green/activate_green_cd",
  C: "CC_Blue/activate_blue_cc",
};

/**
 * Maps feature type letter to looping animation queued after activate.
 * Only Collect has a dedicated update_value loop (it accumulates coin values).
 * Mystery and Double have their own feature-specific sequences (shuffle/transform,
 * loop_multiply/activate_value) managed entirely by the FeatureExecutor.
 */
const FEATURE_TO_UPDATE_ANIM = {
  D: "CD_Green/loop_multiply_green_cd",
  C: "CC_Blue/update_value_blue_cc",
};

/**
 * Maps feature type letter to PotController key in GameViewController.potControllers.
 */
const FEATURE_TO_POT_TYPE = {
  M: "mystery",
  D: "double",
  C: "collect",
};

// ─── Timing Constants ────────────────────────────────────────────────────────

const SPAWN_TRAIL_DURATION = 160;   // ms per coin trail (travel phase)
const SPAWN_COIN_DELAY = 200;       // ms between sequential coin spawns
const ACTIVATION_TRAIL_DURATION = 160; // ms per activation trail (travel phase)
const ACTIVATION_DELAY = 300;          // ms between sequential activations

/**
 * PotCollectionOrbActivator
 *
 * Orchestrates two sequential animation phases during Pot Collection bonus entry:
 *
 * 1. **Coin Ejection** — Trails fire from source POT → coin grid positions.
 *    Each coin plays its `in` animation on landing. Counter increments per coin.
 *
 * 2. **Orb Activation** — For each feature in M→D→C order, trail fires from
 *    POT → Zeus coin on grid. Zeus plays activate animation.
 *
 * Uses FXManager directly for lightning bolt effects (same engine as
 * PotChargingAnimator but in reverse direction: POT→grid instead of grid→POT).
 *
 * GDD: §13.1 steps 4–6, §15 POT order
 */
class PotCollectionOrbActivator {
  /**
   * @param {PIXI.Container} trailContainer - Container for rendering lightning trails
   * @param {object|null} gameViewSpine - The game_view Spine instance for bone lookups
   * @param {number} symbolWidth - Current symbol cell width
   * @param {number} symbolHeight - Current symbol cell height
   */
  constructor(trailContainer, gameViewSpine, symbolWidth, symbolHeight) {
    this.trailContainer = trailContainer;
    this.gameViewSpine = gameViewSpine;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;

    this._activeBolts = [];
    this._cancelled = false;
    this._slamStopRequested = false;
    this._pendingTimers = new Set(); // Timer leak prevention
  }

  // ── Responsive Scale ────────────────────────────────────────────────────────

  _getResponsiveScale() {
    return this.symbolHeight / Settings.SYMBOL_HEIGHT;
  }

  // ── Grid Position Resolver ──────────────────────────────────────────────────

  /**
   * Calculate the grid position of a symbol cell in trailContainer local space.
   *
   * @param {number} reelIndex - 0-based reel column
   * @param {number} symbolIndex - 0-based row within reel
   * @param {number} reelSpacing - Current reel spacing
   * @returns {{x: number, y: number}}
   */
  _getGridPosition(reelIndex, symbolIndex, reelSpacing) {
    const centerOffset = (Settings.REEL_COUNT - 1) / 2;
    const x = reelSpacing * (reelIndex - centerOffset);
    const containerY = -this.symbolHeight * (0.5 * Settings.SYMBOLS_VISIBLE - 0.5);
    const y = containerY + this.symbolHeight * symbolIndex;
    return { x, y };
  }

  // ── POT Bone Position Resolver ──────────────────────────────────────────────

  /**
   * Get POT bone position in trailContainer local space.
   * Same logic as PotChargingAnimator._getPotBonePosition.
   */
  _getPotBonePosition(boneName) {
    const spine = this.gameViewSpine;
    if (!spine || !spine.skeleton) return null;

    const bone = spine.skeleton.findBone(boneName);
    if (!bone) return null;

    _tempPoint.set(bone.worldX, bone.worldY);
    const spineGlobalPos = spine.toGlobal(_tempPoint);

    if (!this.trailContainer || this.trailContainer.destroyed) return null;
    const localPos = this.trailContainer.toLocal(spineGlobalPos);

    return { x: localPos.x, y: localPos.y - this.symbolHeight * 0.15 };
  }

  // ── Trail Firing ────────────────────────────────────────────────────────────

  /**
   * Fire a lightning bolt from startPos → endPos.
   * Delegates to the shared fireLightningBolt engine — same config, timing, and look
   * as the Zeus→Pot bolt in PotChargingAnimator, just reverse direction.
   *
   * @param {{x,y}} startPos
   * @param {{x,y}} endPos
   * @param {number} symbolIndex - Target row (0=top/closest → shorter bolt)
   * @param {number} [duration]
   */
  _fireBolt(startPos, endPos, symbolIndex, duration = SPAWN_TRAIL_DURATION) {
    if (this._cancelled) return Promise.resolve();
    if (!this.trailContainer || this.trailContainer.destroyed) return Promise.resolve();

    return fireLightningBolt(this.trailContainer, startPos, endPos, {
      symbolIndex,
      getDimensions: () => ({ symbolWidth: this.symbolWidth, symbolHeight: this.symbolHeight }),
      duration,
      activeBolts: this._activeBolts,
    });
  }

  /**
   * Spawn coins from source POT to grid positions.
   * Speed modes:
   *   - "normal": sequential bolts with delay between each
   *   - "turbo" (fast mode): all bolts fire simultaneously, wait for all
   *
   * @param {Object} params
   * @param {string} params.respinGameType - e.g. "DMC", "DC", "C"
   * @param {Array<Array<number>>} params.initialReels - [reel][row] initial grid
   * @param {number} params.reelSpacing - Current reel spacing
   * @param {Object} params.overlayReelRefs - PC overlay reel refs
   * @param {Object} params.reelRefs - PC reel refs (for hiding textures)
   * @param {Function} params.onCoinSpawned - Called per coin with {reelIndex, symbolIndex, symbolId}
   * @param {Function} params.onAllSpawned - Called when all coins spawned
   * @param {string} [params.speedMode="normal"] - "normal" or "turbo" (fast mode)
   * @returns {Promise<void>}
   */
  async spawnCoins({
    respinGameType,
    initialReels,
    reelSpacing,
    overlayReelRefs,
    reelRefs,
    potControllers,
    onCoinSpawned,
    onAllSpawned,
    speedMode = "normal",
  }) {
    if (this._cancelled) return;

    // Determine source POT: first in M→D→C order from respinGameType
    const featureOrder = ["M", "D", "C"];
    let sourcePot = null;
    for (const f of featureOrder) {
      if (respinGameType.includes(f)) {
        sourcePot = f;
        break;
      }
    }
    if (!sourcePot) return;

    const potBoneName = FEATURE_TO_POT_BONE[sourcePot];
    // NOTE: potPos is resolved fresh per-bolt below (not cached here) so that
    // resize/orientation changes mid-spawn are reflected in future bolts.

    // Play win animation on the source POT during spawn
    const potType = FEATURE_TO_POT_TYPE[sourcePot];
    const potController = potControllers?.[potType];
    if (potController) {
      potController.playWinLoopWithTransition();
    }

    // Collect all non-Zeus coin positions from initialReels
    // (Zeus orbs are already visible from midpoint — only basic coins need ejection)
    const coinPositions = [];
    for (let reelIndex = 0; reelIndex < initialReels.length; reelIndex++) {
      const reel = initialReels[reelIndex];
      if (!Array.isArray(reel)) continue;
      for (let symbolIndex = 0; symbolIndex < reel.length; symbolIndex++) {
        const symbolId = reel[symbolIndex];
        // Only regular coins and jackpots get ejected (Zeus orbs shown at midpoint)
        if (isCoinSymbol(symbolId) || isJackpotSymbol(symbolId)) {
          coinPositions.push({ reelIndex, symbolIndex, symbolId });
        }
      }
    }

    /**
     * Reveal a single coin at the given position:
     * 1. Show landing → idle animation on overlay spine
     * 2. Hide static blank texture underneath
     * 3. Notify caller (coin counter increment)
     */
    const revealCoin = ({ reelIndex, symbolIndex, symbolId, index }) => {
      // Show coin's landing animation on overlay
      const overlayReelRef = overlayReelRefs?.current?.[reelIndex];
      const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
      if (overlaySymbolRef?.showLandingAnimation) {
        overlaySymbolRef.showLandingAnimation(symbolId);
      }

      // Z-index priority — see getSymbolZIndex for full hierarchy
      if (overlaySymbolRef?.setZIndex) {
        overlaySymbolRef.setZIndex(getSymbolZIndex(symbolId));
      }

      // Hide static texture underneath (spine overlay takes over)
      const reelRef = reelRefs?.current?.[reelIndex];
      const symbolRef = reelRef?.getSymbolRef?.(symbolIndex);
      if (symbolRef?.current?.hideSymbolTexture) {
        symbolRef.current.hideSymbolTexture();
      }

      // Notify caller (counter update, etc.)
      onCoinSpawned?.({ reelIndex, symbolIndex, symbolId, index });
    };

    /**
     * Resolve grid position for a coin from actual overlay container coordinates.
     * Same approach as PotChargingAnimator: toGlobal → trailContainer.toLocal
     * This ensures positions are in the same coordinate space as potPos.
     */
    const resolveGridPosition = (reelIndex, symbolIndex) => {
      const overlayReelRef = overlayReelRefs?.current?.[reelIndex];
      const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(symbolIndex);
      const container = overlaySymbolRef?.getContainer?.();

      if (container && this.trailContainer && !this.trailContainer.destroyed) {
        // Get center of the symbol container in global coords, then to trailContainer local
        _tempPoint.set(0, 0);
        const globalPos = container.toGlobal(_tempPoint);
        const localPos = this.trailContainer.toLocal(globalPos);
        return { x: localPos.x, y: localPos.y };
      }

      // Fallback to math-based position if container not available
      return this._getGridPosition(reelIndex, symbolIndex, reelSpacing);
    };

    if (speedMode === "turbo") {
      // ── FAST MODE: fire all bolts simultaneously, reveal on impact ──
      const promises = coinPositions.map((coin, i) => {
        if (this._cancelled) return Promise.resolve();
        const potPos = this._getPotBonePosition(potBoneName);
        if (!potPos) return Promise.resolve();
        const gridPos = resolveGridPosition(coin.reelIndex, coin.symbolIndex);
        return this._fireBolt(potPos, gridPos, coin.symbolIndex, SPAWN_TRAIL_DURATION).then(() => {
          if (!this._cancelled) revealCoin({ ...coin, index: i });
        });
      });
      await Promise.all(promises);
    } else {
      // ── NORMAL: sequential bolts with cascading delay ──
      for (let i = 0; i < coinPositions.length; i++) {
        if (this._cancelled) return;

        // Reactive slam stop: fire all remaining coins simultaneously
        if (this._slamStopRequested) {
          const remaining = coinPositions.slice(i);
          const promises = remaining.map((coin, j) => {
            if (this._cancelled) return Promise.resolve();
            const slamPotPos = this._getPotBonePosition(potBoneName);
            if (!slamPotPos) return Promise.resolve();
            const gp = resolveGridPosition(coin.reelIndex, coin.symbolIndex);
            return this._fireBolt(slamPotPos, gp, coin.symbolIndex, SPAWN_TRAIL_DURATION).then(() => {
              if (!this._cancelled) revealCoin({ ...coin, index: i + j });
            });
          });
          await Promise.all(promises);
          break;
        }

        const coin = coinPositions[i];
        // Resolve potPos fresh each iteration — orientation may have changed
        const currentPotPos = this._getPotBonePosition(potBoneName);
        const gridPos = resolveGridPosition(coin.reelIndex, coin.symbolIndex);

        if (currentPotPos) {
          await this._fireBolt(currentPotPos, gridPos, coin.symbolIndex, SPAWN_TRAIL_DURATION);
        }

        if (this._cancelled) return;
        revealCoin({ ...coin, index: i });

        // Delay between coins (and after the last one before activation)
        await this._delay(SPAWN_COIN_DELAY);
      }
    }

    onAllSpawned?.();
  }

  // ── Phase 2: Orb Activation ─────────────────────────────────────────────────

  /**
   * Activate Zeus orbs sequentially in M→D→C order.
   * For each feature in activationQueue:
   *   1. Trail fires from matching POT → Zeus coin on grid
   *   2. Zeus coin plays activate animation
   *
   * activationQueue entries can be:
   *   - string: feature letter (e.g. "M") — legacy, scans grid for position
   *   - object: { feature, reelIndex, symbolIndex } — pre-resolved position (multi-Zeus)
   *
   * @param {Object} params
   * @param {Array<string|{feature: string, reelIndex: number, symbolIndex: number}>} params.activationQueue
   * @param {Array<Array<number>>} [params.initialReels] - Grid data (only needed for legacy string entries)
   * @param {number} params.reelSpacing - Current reel spacing
   * @param {Object} params.overlayReelRefs - PC overlay reel refs
   * @param {Function} [params.onActivated] - Called per feature with { feature, reelIndex, symbolIndex }
   * @param {Function} [params.onFeatureReady] - Async callback per feature AFTER activate animation.
   *   Awaited before moving to the next feature. Receives { feature, reelIndex, symbolIndex }.
   *   This is where the control context chains into FeatureExecutor.
   * @param {Function} [params.onAllActivated] - Called when ALL features are activated and executed
   * @param {boolean} [params.slamStop=false] - Skip animations
   * @param {string} [params.speedMode="normal"] - "normal" or "turbo"
   * @returns {Promise<void>}
   */
  async activateOrbs({
    activationQueue,
    initialReels,
    reelSpacing,
    overlayReelRefs,
    potControllers,
    skipPositions,
    onActivated,
    onFeatureReady,
    onAllActivated,
    slamStop = false,
    speedMode = "normal",
  }) {
    if (this._cancelled) return;

    // Fast mode = skip inter-activation delays. Computed once but also checked reactively via _slamStopRequested.
    // Note: activation bolts + animate always play regardless — only delays are skippable.
    const initialSkipDelays = slamStop || speedMode === "turbo";

    // Track positions used within THIS activation pass (for same-type dedup within the grid scan fallback)
    const usedPositions = new Set();

    for (let i = 0; i < activationQueue.length; i++) {
      if (this._cancelled) return;

      // Normalize entry: string → { feature }, object → pass through
      const entry = typeof activationQueue[i] === "string"
        ? { feature: activationQueue[i] }
        : activationQueue[i];

      const feature = entry.feature;
      const zeusIds = FEATURE_TO_ZEUS_IDS[feature];
      const activateAnim = FEATURE_TO_ACTIVATE_ANIM[feature];
      const potBoneName = FEATURE_TO_POT_BONE[feature];

      if (!zeusIds || !activateAnim || !potBoneName) {
        continue;
      }

      let zeusPos = null;
      let zeusReelIndex = -1;
      let zeusSymbolIndex = -1;

      if (entry.reelIndex !== undefined && entry.symbolIndex !== undefined) {
        // ── Pre-resolved position (multi-Zeus support) ──
        zeusReelIndex = entry.reelIndex;
        zeusSymbolIndex = entry.symbolIndex;

        // Resolve position in trailContainer space
        const overlayReelRef = overlayReelRefs?.current?.[zeusReelIndex];
        const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(zeusSymbolIndex);
        const container = overlaySymbolRef?.getContainer?.();

        if (container && this.trailContainer && !this.trailContainer.destroyed) {
          _tempPoint.set(0, 0);
          const globalPos = container.toGlobal(_tempPoint);
          const localPos = this.trailContainer.toLocal(globalPos);
          zeusPos = { x: localPos.x, y: localPos.y };
        } else {
          zeusPos = this._getGridPosition(zeusReelIndex, zeusSymbolIndex, reelSpacing);
        }
      } else {
        // ── Legacy: scan grid for Zeus position ──
        for (let ri = 0; ri < (initialReels || []).length; ri++) {
          const reel = initialReels[ri];
          if (!Array.isArray(reel)) continue;
          for (let si = 0; si < reel.length; si++) {
            if (zeusIds.includes(parseInt(reel[si], 10))) {
              const posKey = `${ri}_${si}`;
              // Skip positions already activated (both global and this-pass)
              if (skipPositions && skipPositions.has(posKey)) continue;
              if (usedPositions.has(posKey)) continue;

              zeusReelIndex = ri;
              zeusSymbolIndex = si;

              const overlayReelRef = overlayReelRefs?.current?.[ri];
              const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(si);
              const container = overlaySymbolRef?.getContainer?.();

              if (container && this.trailContainer && !this.trailContainer.destroyed) {
                _tempPoint.set(0, 0);
                const globalPos = container.toGlobal(_tempPoint);
                const localPos = this.trailContainer.toLocal(globalPos);
                zeusPos = { x: localPos.x, y: localPos.y };
              } else {
                zeusPos = this._getGridPosition(ri, si, reelSpacing);
              }
              break;
            }
          }
          if (zeusPos) break;
        }
      }

      if (!zeusPos) {
        continue;
      }

      // Mark this position as used (for legacy fallback dedup)
      usedPositions.add(`${zeusReelIndex}_${zeusSymbolIndex}`);

      // Reactive check: if slam stop was requested mid-loop, skip inter-activation delays.
      const skipDelays = initialSkipDelays || this._slamStopRequested;

      // ── ACTIVATION SEQUENCE (never skippable) ──
      // 1. POT win loop + bolt from POT → Zeus
      // 2. Zeus activate animation (awaited)
      // These always play regardless of slamStop/turbo — user requirement.

      // Only start pot win loop if this pot isn't already looping win
      // (i.e., previous feature was a different type or this is the first)
      const potType = FEATURE_TO_POT_TYPE[feature];
      const potController = potControllers?.[potType];
      const prevFeature = i > 0
        ? (typeof activationQueue[i - 1] === "string" ? activationQueue[i - 1] : activationQueue[i - 1].feature)
        : null;
      if (potController && prevFeature !== feature) {
        potController.playWinLoopWithTransition();
      }

      const potPos = this._getPotBonePosition(potBoneName);
      if (potPos) {
        await this._fireBolt(potPos, zeusPos, zeusSymbolIndex, ACTIVATION_TRAIL_DURATION);
      }

      if (this._cancelled) return;

      // Play activate animation on Zeus overlay.
      // Await the custom 'activate_ready' event (embedded in cs.json at t=0.33s,
      // right after the bolt flash fades) before handing off to FeatureExecutor.
      // This lets the visual impact play without the long dead gap of the full 1.5s anim.
      const overlayReelRef = overlayReelRefs?.current?.[zeusReelIndex];
      const overlaySymbolRef = overlayReelRef?.getSymbolRef?.(zeusSymbolIndex);
      if (overlaySymbolRef?.playNamedAnimation) {
        overlaySymbolRef.playNamedAnimation(activateAnim, false, null, zeusIds[0]);

        // Queue update animation IMMEDIATELY — Spine chains it after activate
        // with zero gap. Previously queued in onComplete which caused a 1-frame
        // flash (setup pose visible between activate end and update start).
        const updateAnim = FEATURE_TO_UPDATE_ANIM[feature];
        if (updateAnim && overlaySymbolRef.queueNamedAnimation) {
          overlaySymbolRef.queueNamedAnimation(updateAnim, true);
        }

        // Wait for 'activate_ready' event before proceeding to feature execution
        const csSpine = overlaySymbolRef.getController?.()?.prePositionedSpines?.get("cs");
        if (csSpine?.state) {
          await this._waitForSpineEvent(csSpine, "activate_ready");
        }
      }

      if (this._cancelled) return;

      onActivated?.({ feature, reelIndex: zeusReelIndex, symbolIndex: zeusSymbolIndex });

      // Await feature execution (Mystery shuffle, Double trails, Collect trails)
      // The control context chains FeatureExecutor here via onFeatureReady.
      // Pass reactive slamStop so features can check it mid-execution.
      if (onFeatureReady) {
        await onFeatureReady({
          feature,
          reelIndex: zeusReelIndex,
          symbolIndex: zeusSymbolIndex,
          queueIndex: i,
          slamStop: this._slamStopRequested,
        });
      }

      if (this._cancelled) return;

      // Only crossfade pot to idle if the NEXT feature is a different type (or last)
      // — avoids redundant win→idle→win cycles for consecutive same-type features
      const nextFeature = i < activationQueue.length - 1
        ? (typeof activationQueue[i + 1] === "string" ? activationQueue[i + 1] : activationQueue[i + 1].feature)
        : null;
      if (potController && nextFeature !== feature) {
        potController.playIdleFromWin();
      }

      // Delay between activations (unless skip mode or last one)
      if (!skipDelays && !this._slamStopRequested && i < activationQueue.length - 1) {
        await this._delay(ACTIVATION_DELAY);
      }
    }

    onAllActivated?.();
  }

  // ── Utility ─────────────────────────────────────────────────────────────────

  /**
   * Wait for a named event embedded in a spine animation timeline.
   * Resolves as soon as the event fires. Safety timeout (2s) prevents hanging
   * if the event is never emitted (e.g. animation was replaced before reaching it).
   */
  _waitForSpineEvent(spine, eventName) {
    if (this._cancelled) return Promise.resolve();
    return new Promise((resolve) => {
      let resolved = false;
      const done = () => {
        if (resolved) return;
        resolved = true;
        safetyTid.clear();
        this._pendingTimers.delete(safetyTid);
        try { spine.state.removeListener(listener); } catch (_) {}
        resolve();
      };

      const listener = {
        event: (entry, event) => {
          if (event.data.name === eventName) {
            done();
          }
        },
      };
      spine.state.addListener(listener);

      // Safety timeout — don't hang forever if animation gets replaced
      const safetyTid = new TickerTimeout(done, 2000);
      this._pendingTimers.add(safetyTid);
    });
  }

  _delay(ms) {
    if (this._cancelled || this._slamStopRequested) return Promise.resolve();
    return new Promise((resolve) => {
      const tid = new TickerTimeout(() => {
        this._pendingTimers.delete(tid);
        resolve();
      }, ms);
      this._pendingTimers.add(tid);
    });
  }

  /**
   * Request slam stop — skip bolt animations and delays but still process all features.
   * Unlike cancel(), this does NOT abort feature execution.
   */
  requestSlamStop() {
    this._slamStopRequested = true;
  }

  // ── Cancel / Cleanup ────────────────────────────────────────────────────────

  cancel() {
    this._cancelled = true;

    // Clear all pending delay timers
    for (const tid of this._pendingTimers) {
      if (tid && typeof tid.clear === "function") tid.clear();
    }
    this._pendingTimers.clear();

    for (const bolt of this._activeBolts) {
      if (bolt.tickerFn) {
        PIXI.Ticker.shared.remove(bolt.tickerFn);
        bolt.tickerFn = null;
      }
      FXManager.releaseOwnedFx(bolt.boltData);
      // Clean up launch and impact ball emitters
      for (const ball of [bolt.launchBall, bolt.impactBall]) {
        FXManager.releaseOwnedFx(ball);
      }
      bolt.resolve?.();
    }
    this._activeBolts = [];
  }

  resize(symbolWidth, symbolHeight) {
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
  }

  setSpine(spine) {
    this.gameViewSpine = spine;
  }

  destroy() {
    this.cancel();
    this.trailContainer = null;
    this.gameViewSpine = null;
  }
}

export default PotCollectionOrbActivator;
